import { z } from "zod";
import { businessDTO, clientUserDTO } from "../../Types-DTOs/ClientUser";
import { ChatDTO } from "../../Types-DTOs/Chat";
import { InvitationDTO } from "../../Types-DTOs/Invitation";
import { loginIntentDto } from "../../Types-DTOs/LoginIntent";
import { BillsDto } from "../../Types-DTOs/Bills";
import { CustomerBillingDto, DetailedSubscriptionDto } from "../../Types-DTOs/DetailedSubscription";
import {
  dueDiligenceCaseSchema,
  dueDiligenceDocumentSchema,
  dueDiligenceReportNoteSchema,
  dueDiligenceTierSchema,
} from "../../Types-DTOs/DueDiligence";
import { contractSchema, contractSummarySchema, contractTemplateSummarySchema } from "../../Types-DTOs/Contracts";
import { addressSuggestionSchema } from "../../Types-DTOs/Address";
import { SUGGESTION_RISK_VALUES } from "@dd-report-components";

export const errorSchema = z.object({
  message: z.string(),
  errorCode: z.string(),
  traceId: z.string().optional(),
  fields: z.record(z.array(z.string())).optional(),
});

// HTTP status is attached by the request helper (not part of the response
// body), so callers can discriminate 4xx/5xx/network without inspecting the
// backend errorCode string.
export type ApiError = z.infer<typeof errorSchema> & { status?: number };

export const getChatCreditsSchema = z.object({
  message: z.string(),
  unlimited: z.boolean(),
  availableChatCredits: z.number(),
});

export const referralSchema = z.object({
  referralCode: z.string(),
  referredBusinesses: z.array(
    z.object({
      name: z.string(),
      registeredAt: z.coerce.date(),
      creditsEarned: z.number(),
    })
  ),
  creditsEarned: z.number(),
});

export const getReferralSchema = z.object({
  message: z.string(),
  referral: referralSchema,
});

export type Referral = z.infer<typeof referralSchema>;

export const creditTransactionTypes = [
  "SUBSCRIPTION_GRANT",
  "CREDIT_PURCHASE",
  "PROMOTIONAL_GRANT",
  "DD_CONSUMPTION",
  "EXPIRATION",
  "REFUND",
  "ADMIN_ADJUSTMENT",
  "REWARD_FEEDBACK",
  "REWARD_REFERRAL",
  "REWARD_REFERRAL_WELCOME",
  "REWARD_TESTIMONIAL",
  "REWARD_BLOG",
] as const;

export const creditTransactionSchema = z.object({
  id: z.string(),
  amount: z.number(),
  type: z.enum(creditTransactionTypes),
  createdAt: z.string().transform((date) => new Date(date)),
  expiresAt: z.string().nullable().transform((date) => (date ? new Date(date) : null)),
  dueDiligenceCase: z.object({ id: z.string(), name: z.string(), alias: z.string() }).nullable(),
});

export type CreditTransaction = z.infer<typeof creditTransactionSchema>;

export const getCreditTransactionsSchema = z.object({
  message: z.string(),
  transactions: z.array(creditTransactionSchema),
  nextCursor: z.string().nullable(),
});

export const dueDiligencePlanOfferSchema = z.object({
  lookupKey: z.string(),
  monthlyCredits: z.number(),
  includesChat: z.boolean(),
  /** The DD tiers its credits pay for; null for every tier. */
  allowedTiers: z.array(dueDiligenceTierSchema).nullable().optional(),
  /** unitAmount in cents, VAT excluded */
  price: z.object({ unitAmount: z.number(), currency: z.string() }),
});

export type DueDiligencePlanOffer = z.infer<typeof dueDiligencePlanOfferSchema>;

export const getDueDiligencePlansSchema = z.object({
  message: z.string(),
  plans: z.array(dueDiligencePlanOfferSchema),
});

export const getDueDiligencePlanCheckoutSchema = z.object({
  message: z.string(),
  url: z.string(),
});

export const getCreditsCheckoutSchema = z.object({
  message: z.string(),
  url: z.string(),
});

export const dueDiligencePlanChangePreviewSchema = z.object({
  /** Charged right away, in cents, taxes included. */
  amountDue: z.number(),
  currency: z.string(),
  /** The credits the change brings now: the difference between both plans. */
  extraMonthlyCredits: z.number(),
});

export type DueDiligencePlanChangePreview = z.infer<typeof dueDiligencePlanChangePreviewSchema>;

export const getDueDiligencePlanChangePreviewSchema = z.object({
  message: z.string(),
  preview: dueDiligencePlanChangePreviewSchema,
});

export const changeDueDiligencePlanSchema = z.object({
  message: z.string(),
});

export const getChatCreditPackPaymentUrlSchema = z.object({
  message: z.string(),
  paymentUrl: z.string(),
});

export const registerUserSchema = z.object({
  clientUser: clientUserDTO,
  token: z.string(),
});

export const loginSchema = z.object({
  clientUser: clientUserDTO,
  token: z.string(),
});

export const getBusinessSchema = z.object({
  business: businessDTO,
});

export const inviteUserSchema = z.object({
  business: businessDTO,
});

export const removeMemberSchema = z.object({
  business: businessDTO,
});

export const updateMemberRoleSchema = z.object({
  business: businessDTO,
});

export const createChatSchema = z.object({
  message: z.string(),
  chat: ChatDTO,
  userMessageId: z.string().optional(),
});

export const getChatByIdSchema = z.object({
  chat: ChatDTO,
});

export const sendMessageSchema = z.object({
  message: z.string(),
  chat: ChatDTO,
  userMessageId: z.string().optional(),
});

export const setMessageFeedbackSchema = z.object({
  message: z.string(),
  feedback: z.union([z.literal(1), z.literal(-1), z.literal(0)]).nullable(),
});

export const sendDocumentSchema = z.object({
  message: z.string(),
  chat: ChatDTO,
  userMessageId: z.string().optional(),
});

export const getAllChatsSchema = z.object({
  conversations: z.array(ChatDTO),
  traceId: z.string(),
});

export const useInvitationSchema = z.object({
  clientUser: clientUserDTO,
  token: z.string(),
});

export const useInvitationWithSsoSchema = z.object({
  clientUser: clientUserDTO,
  token: z.string(),
});

export const getInvitationSchema = z.object({
  invitation: InvitationDTO,
});

export const agentLoginIntentSchema = z.object({
  loginIntent: loginIntentDto,
});

export const agentLoginSchema = z.object({
  clientUser: clientUserDTO,
  token: z.string(),
});

export const getPaymentUrlSchema = z.object({
  paymentUrl: z.string(),
});

export const getPaymentCustomerSessionSchema = z.object({
  customerSecret: z.string(),
});

export const updateAllowedMembersSchema = z.object({
  business: businessDTO,
});

export const getBillsSchema = z.object({
  bills: z.array(BillsDto),
});

export const getCancelUrlSchema = z.object({
  cancelSubscriptionUrl: z.string(),
});

export const getCustomerPortalUrlSchema = z.object({
  customerPortalUrl: z.string(),
});

export const getDetailedSubscriptionSchema = z.object({
  subscription: DetailedSubscriptionDto.nullable(),
  trial: z.object({
    endDate: z.string().transform((date) => new Date(date)),
    isActive: z.boolean(),
  }).nullable(),
  billing: CustomerBillingDto.nullable(),
});

export const getInvitesSchema = z.object({
  invites: z.array(InvitationDTO),
});

// Due Diligence schemas
export const createDueDiligenceCaseSchema = z.object({
  message: z.string(),
  case: dueDiligenceCaseSchema,
  paymentUrl: z.string().optional(),
});

export const dueDiligenceTaskStatusSchema = z.object({
  label: z.string(),
  completed: z.boolean(),
});
export type DueDiligenceTaskStatus = z.infer<typeof dueDiligenceTaskStatusSchema>;

export const getDueDiligenceCaseSchema = z.object({
  message: z.string(),
  case: dueDiligenceCaseSchema,
  tasks: z.array(dueDiligenceTaskStatusSchema).optional(),
});

export const listDueDiligenceCasesSchema = z.object({
  message: z.string(),
  cases: z.array(dueDiligenceCaseSchema),
});

export const listContractsSchema = z.object({
  message: z.string(),
  contracts: z.array(contractSummarySchema),
});

export const getContractSchema = z.object({
  message: z.string(),
  contract: contractSchema,
});

export const createContractSchema = getContractSchema;

export const rejectedContractVariableSchema = z.object({
  key: z.string(),
  label: z.string(),
  reason: z.string(),
});
export type RejectedContractVariable = z.infer<typeof rejectedContractVariableSchema>;

export const updateContractVariablesSchema = z.object({
  message: z.string(),
  contract: contractSchema,
  rejected: z.array(rejectedContractVariableSchema).optional().default([]),
});

export const listContractTemplatesSchema = z.object({
  message: z.string(),
  templates: z.array(contractTemplateSummarySchema),
});

export const updateDueDiligenceCaseSchema = z.object({
  message: z.string(),
  case: dueDiligenceCaseSchema,
});

// Document schemas
export const uploadDueDiligenceDocumentSchema = z.object({
  message: z.string(),
  document: dueDiligenceDocumentSchema,
});

export const listDueDiligenceDocumentsSchema = z.object({
  message: z.string(),
  documents: z.array(dueDiligenceDocumentSchema),
});

export const deleteDueDiligenceDocumentSchema = z.object({
  message: z.string(),
});

export const getDocumentSignedUrlSchema = z.object({
  message: z.string(),
  signedUrl: z.string(),
  expiresAt: z.string(),
});

export const getReportFilesSchema = z.object({
  files: z.record(
    z.object({
      publicName: z.string().nullable(),
      fileName: z.string(),
      signedUrl: z.string(),
    })
  ),
});

// Lazy per-section report API. `data` stays loose (z.any): the component reads
// meta/sections (manifest) or the section blob directly.
export const getReportManifestSchema = z.object({
  message: z.string(),
  status: z.enum(['ready', 'pending']),
  data: z.any().nullable(),
  retryAfterSeconds: z.number().optional(),
  traceId: z.string().optional(),
});

export const getReportNumberedSectionSchema = getReportManifestSchema;

export const listDueDiligenceReportNotesSchema = z.object({
  notes: z.array(dueDiligenceReportNoteSchema),
});

export const createDueDiligenceReportNoteSchema = z.object({
  note: dueDiligenceReportNoteSchema,
});

export const updateDueDiligenceReportNoteSchema = createDueDiligenceReportNoteSchema;

export const updateCasePdfSettingsSchema = z.object({
  message: z.string(),
});

export const getDueDiligencePaymentUrlSchema = z.object({
  message: z.string(),
  paymentUrl: z.string(),
});

export const getDueDiligenceUpsellPaymentUrlSchema = getDueDiligencePaymentUrlSchema;

/** The credits taken off the euros a DD costs, and the ones applying now would take off. */
export const getDueDiligenceCreditDiscountSchema = z.object({
  credits: z.number(),
  /** The euros those credits took off. */
  amount: z.number(),
  creditsToApply: z.number(),
});

/** The credits just taken off, and the case: paid when they covered it whole. */
export const applyDueDiligenceCreditDiscountSchema = z.object({
  credits: z.number(),
  /** The euros they took off. */
  amount: z.number(),
  case: dueDiligenceCaseSchema,
});

export const getDueDiligenceUpsellQuoteSchema = z.object({
  amountToPay: z.number(),
});

export const translateReportSchema = z.object({
  message: z.string(),
  status: z.literal('started'),
});

export const getDueDiligencePricingSchema = z.object({
  pricing: z.object({
    basic: z.number().nullable(),
    professional: z.number().nullable(),
    premium: z.number().nullable(),
  }),
  /** What each tier costs paid with credits. */
  creditCosts: z.object({
    basic: z.number(),
    professional: z.number(),
    premium: z.number(),
  }),
  /** What a credit is worth in euros. */
  creditValue: z.number().nullish(),
});

export const getDueDiligenceChatSuggestionsSchema = z.object({
  message: z.string(),
  suggestions: z.array(
    z.object({
      text: z.string(),
      risk: z.enum(SUGGESTION_RISK_VALUES),
    })
  ),
});

export const feedbackQuestionSchema = z.object({
  id: z.string(),
  type: z.enum(["text", "single_choice"]),
  label: z.string(),
  options: z.array(z.string()).optional(),
  order: z.number(),
});

export const dueDiligenceFeedbackQuestionsConfigSchema = z.object({
  npsLabel: z.string(),
  questions: z.array(feedbackQuestionSchema),
});

export const getDueDiligenceFeedbackQuestionsSchema = z.object({
  config: dueDiligenceFeedbackQuestionsConfigSchema,
});

export const getDueDiligenceFeedbackStatusSchema = z.object({
  alreadySubmitted: z.boolean(),
  shouldAutoPrompt: z.boolean(),
});

export const submitDueDiligenceFeedbackSchema = z.object({
  message: z.string(),
  // What the feedback earned the business; absent when giving it failed.
  creditsGranted: z.number().optional(),
});

export type FeedbackQuestion = z.infer<typeof feedbackQuestionSchema>;
export type DueDiligenceFeedbackQuestionsConfig = z.infer<typeof dueDiligenceFeedbackQuestionsConfigSchema>;
export type DueDiligenceFeedbackStatus = z.infer<typeof getDueDiligenceFeedbackStatusSchema>;

// Push Notifications
export const vapidPublicKeySchema = z.object({
  publicKey: z.string(),
});

// Platform Config
export const platformConfigSchema = z.object({
  chatSseEnabled: z.boolean(),
});

export const getClientUserSchema = z.object({
  clientUser: clientUserDTO.extend({
    completedTours: z.array(z.string()),
  }),
});
export const suggestAddressesSchema = z.object({
  suggestions: z.array(addressSuggestionSchema),
});
